import React, { useState, lazy, Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mediaCenterUrl, PATHS, enterpriseUrl } from 'config';
import LandingPage from 'feature/common/landing';
import PageNotFound from 'feature/common/page-not-found';
import { PoiContextProvider } from 'feature/poi-module/use-poi';
import { UserProvider } from 'feature/common/user/use-user';
import RedirectPage from 'feature/common/redirect';
import { Menu, SubMenu } from 'components/navbar/navbar';
import AppLayout from 'components/layout/app-layout';
import Navbar from 'components/navbar';
import Footer from 'components/footer';
import { ProviderRouter } from 'utils/provider-helper';
import { currentUrl } from 'utils/url-helper';
import GasPrice from 'feature/common/gas-price';
import enterpriseIcon from 'images/icon_B.svg';
import amplitudeService from 'service/amplitude';

const RefuelingPage = lazy(() => import('feature/common/refueling'));
const ParkingPage = lazy(() => import('feature/common/parking'));
const OnStreetParkingPage = lazy(() => import('feature/common/on-street-parking'));
const AutoDetailingPage = lazy(() => import('feature/common/auto-detailing'));
const GasStationPage = lazy(() => import('feature/common/gas-station'));
const ParkinglotsPage = lazy(() => import('feature/common/parkinglots'));
const CarBeautyStoresPage = lazy(() => import('feature/common/car-beauty-stores'));
const AutoRepairStoresPage = lazy(() => import('feature/common/auto-repair-stores'));
const CarWashStoresPage = lazy(() => import('feature/common/car-wash-stores'));
const PromotionPage = lazy(() => import('feature/common/promotion'));
const PromotionServicePage = lazy(() => import('feature/common/promotion/promotion-service'));
const PromotionDetailPage = lazy(() => import('feature/common/promotion/detail'));
const PromotionGroupPage = lazy(() => import('feature/common/promotion/group'));
const UserPage = lazy(() => import('feature/common/user'));

export const EnterpriseLink = ({ icon = true }) => {
  return (
    <div
      style={{ display: 'flex' }}
      onClick={() => {
        amplitudeService.track('企業方案入口_點擊');
      }}
    >
      企業方案
      {icon && <img style={{ marginLeft: 4 }} src={enterpriseIcon} alt="enterprise" />}
    </div>
  );
};

const MochipayRouter = () => {
  const { t } = useTranslation();
  const [isPromotionService, setIsPromotionService] = useState();

  const navbarMenu = [
    new Menu(t('navbar.refueling'), PATHS.REFUELING),
    new Menu(t('navbar.parking'), PATHS.PARKING),
    new Menu(t('navbar.on_street_parking'), PATHS.ON_STREET_PARKING),
    new Menu(t('navbar.auto_detailing'), PATHS.AUTO_DETAILING),
    new SubMenu(t('navbar.location'), [
      new Menu(t('navbar.gas_station'), PATHS.GAS_STATIONS, true, 'gas-station'),
      new Menu(t('navbar.parking_lot'), PATHS.PARKINGLOTS, true, 'parkinglot'),
      // new Menu(t('navbar.car_beauty'), PATHS.CAR_BEAUTY_STORES, true, 'car-beauty-store'),
      new Menu(t('navbar.auto_repair'), PATHS.AUTO_REPAIR_STORES, true, 'auto-repair-store'),
      // new Menu(t('navbar.car_wash'), PATHS.CAR_WASH_STORES, true, 'car-wash-store'),
    ]),
    new Menu(t('navbar.promotion'), PATHS.PROMOTION),
    new Menu(t('navbar.help'), 'https://help.mochipay.xyz/', false),
    new Menu((<EnterpriseLink />), enterpriseUrl(), false),
  ];

  const footerMenu = [
    new SubMenu(t('footer.about'), [
      new Menu(t('footer.refueling'), PATHS.REFUELING),
      new Menu(t('footer.parking'), PATHS.PARKING),
      new Menu(t('footer.on_street_parking'), PATHS.ON_STREET_PARKING),
      new Menu(t('footer.auto_detailing'), PATHS.AUTO_DETAILING),
    ]),
    new SubMenu(t('footer.location'), [
      new Menu(t('footer.gas_station'), PATHS.GAS_STATIONS),
      new Menu(t('footer.parking_lot'), PATHS.PARKINGLOTS),
      // new Menu(t('footer.car_beauty'), PATHS.CAR_BEAUTY_STORES),
      new Menu(t('footer.auto_repair'), PATHS.AUTO_REPAIR_STORES),
      // new Menu(t('footer.car_wash'), PATHS.CAR_WASH_STORES),
    ]),
    new SubMenu(t('footer.support'), [
      new Menu(t('footer.promotion'), PATHS.PROMOTION),
      new Menu(t('footer.help'), 'https://help.mochipay.xyz/', false),
      new Menu((<EnterpriseLink icon={false} />), enterpriseUrl(), false),
    ]),
    new SubMenu(t('footer.newsroom'), [
      new Menu(t('footer.about_us'), `${mediaCenterUrl()}/company-info`, false),
      new Menu(t('footer.latest_news'), `${mediaCenterUrl()}/press-release`, false),
      new Menu(
        t('footer.media_coverages'),
        'https://docs.google.com/document/d/1TxMC54L3Dk3RDXgX-WHNMuP07N9RAEARiSsCaGf6FAA/edit?usp=sharing',
        false
      ),
      new Menu(
        t('footer.brand_resources'),
        'https://drive.google.com/drive/u/0/folders/1shEY6IQe5v62pT3eD2M-9UE6kgxp1LEc',
        false
      ),
    ]),
  ];

  const navbarMobileMenu = [
    {
      title: t('navbar.about'),
      menus: [
        new Menu(t('navbar.refueling'), PATHS.REFUELING),
        new Menu(t('navbar.parking'), PATHS.PARKING),
        new Menu(t('navbar.on_street_parking'), PATHS.ON_STREET_PARKING),
        new Menu(t('navbar.auto_detailing'), PATHS.AUTO_DETAILING),
      ],
    },
    {
      title: t('navbar.location'),
      menus: [
        new Menu(t('navbar.gas_station'), PATHS.GAS_STATIONS, true, 'gas-station'),
        new Menu(t('navbar.auto_repair'), PATHS.AUTO_REPAIR_STORES, true, 'auto-repair-store'),
        new Menu(t('navbar.parking_lot'), PATHS.PARKINGLOTS, true, 'parkinglot'),
        // new Menu(t('navbar.car_wash'), PATHS.CAR_WASH_STORES, true, 'car-wash-store'),
        // new Menu(t('navbar.car_beauty'), PATHS.CAR_BEAUTY_STORES, true, 'car-beauty-store'),
      ],
    },
    {
      title: t('navbar.support'),
      menus: [
        new Menu(t('navbar.promotion'), PATHS.PROMOTION),
        new Menu(t('navbar.help'), 'https://help.mochipay.xyz/', false),
        new Menu((<EnterpriseLink />), enterpriseUrl(), false),
      ],
    },
  ];

  const RouterContainer = ({ children }) => {
    return (
      <>
        <Navbar menus={navbarMenu} mobileMenus={navbarMobileMenu} />
        {children}
        <Footer menus={footerMenu} />
      </>
    );
  };

  return (
    <AppLayout>
      <Suspense fallback={''}>
        <Switch>
          {/* 重新導向舊有 auto-repair 連結 */}
          <Route exact path="/auto-repair">
            <Redirect to={PATHS.AUTO_DETAILING} />
          </Route>
          <Route
            exact
            path={currentUrl('/')}
            component={() => (
              <RouterContainer>
                <LandingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.REFUELING)}
            component={() => (
              <RouterContainer>
                <RefuelingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.PARKING)}
            component={() => (
              <RouterContainer>
                <ParkingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.ON_STREET_PARKING)}
            component={() => (
              <RouterContainer>
                <OnStreetParkingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.AUTO_DETAILING)}
            component={() => (
              <RouterContainer>
                <AutoDetailingPage />
              </RouterContainer>
            )}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <GasStationPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.GAS_STATIONS)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <ParkinglotsPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.PARKINGLOTS)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <CarBeautyStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.CAR_BEAUTY_STORES)}
          />

          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <AutoRepairStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.AUTO_REPAIR_STORES)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <CarWashStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.CAR_WASH_STORES)}
          />
          <Route
            exact
            path={currentUrl(PATHS.PROMOTION)}
            component={() => (
              <RouterContainer>
                <PromotionPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.PROMOTION_DETAIL)}
            component={() => {
              if (isPromotionService) return <PromotionDetailPage />;

              return (
                <RouterContainer>
                  <PromotionDetailPage />
                </RouterContainer>
              );
            }}
          />
          <Route
            path={currentUrl(PATHS.PROMOTION_GROUP)}
            component={() => {
              return (
                <RouterContainer>
                  <PromotionGroupPage />
                </RouterContainer>
              );
            }}
          />
          <ProviderRouter
            provider={UserProvider}
            component={() => (
              <RouterContainer>
                <UserPage />
              </RouterContainer>
            )}
            path={PATHS.USER}
          />
          <Route path={PATHS.REDIRECT} component={RedirectPage} />
          {/* 繳費機線上化停車優惠 */}
          <Route
            path={`${PATHS.PROMOTION}/:serviceType`}
            component={() => {
              setIsPromotionService(true);
              return (
                <>
                  <PromotionServicePage />
                </>
              );
            }}
          />
          <Route path={`${PATHS.GAS_PRICE}`} component={GasPrice} />
          <Route path="*" component={PageNotFound} />
        </Switch>
      </Suspense>
    </AppLayout>
  );
};

export default MochipayRouter;
